import * as actionTypes from '../../../../models/actionTypes';
import {
  Modality,
  UserInfo,
  ClientTokenRetrievalInfoModel,
  AuthInfo,
  AlchemyResult,
  AlchemyThemeRoutingData,
} from '../../../../models/apiModels';
import { ServiceRequestPage, ServiceRequestPageType } from '../../../../models/reduxState';
import { ClientPromptForLoginState } from '../../../../utilities';
import { ModalityResponseModel } from '../../models/modalityModels';
import { FreelanceRequest } from '../../models/requestModels';

export const initializeModalityStateOnGetHelp = (
  modalities: Modality[]
) => {
  return {
    type: actionTypes.INIT_MODALITY_SECTION_ON_SESSION_START,
    modalities,
  };
};

export const redirectForSignInLinkFetch = () => {
  return {
    type: actionTypes.REDIRECT_FOR_SIGN_IN,
  };
};

export const fetchAlchemyThemeRoutingDataSuccess = () => {
  return {
    type: actionTypes.GET_ALCHEMY_THEME_ROUTING_DATA_SUCCESS,
  };
};

export const fetchRedirectForSignInLinkSuccess = (
  signInLink: string
) => {
  return {
    type: actionTypes.FETCH_REDIRECT_LINK_SUCCESS,
    redirectForSignInLink: signInLink,
  };
};

export const setAuthInfoForTeams = (
  authInfo: AuthInfo
) => {
  return {
    type: actionTypes.SET_AUTHINFO_FOR_TEAMS,
    authInfo,
  };
};

export const fetchUserInfo = () => {
  return {
    type: actionTypes.FETCH_USERINFO,
  };
};

export const fetchUserInfoForClientSignIn = () => {
  return {
    type: actionTypes.FETCH_USERINFO_FOR_CLIENT_SIGNIN,
  };
};

export const setUserIsSigningIntoClientApp = (
  spinnerStatus: boolean
) => {
  return {
    type: actionTypes.SET_IS_USER_SIGNING_INTO_CLIENT_APP,
    spinnerStatus,
  };
};

export const fetchUserInfoSuccess = (
  userInfo: UserInfo
) => {
  if (userInfo && userInfo.Cid) {
    return {
      type: actionTypes.FETCH_USERINFO_SUCCESS,
      userInfo,
    };
  }

  return {
    type: actionTypes.FETCH_USERINFO_NOT_SIGNEDIN,
  };
};

export const fetchUserInfoAfterSignInForClient = (
  userInfo: UserInfo
) => {
  if (userInfo && userInfo.Cid) {
    return {
      type: actionTypes.FETCH_USERINFO_SUCCESS,
      userInfo,
    };
  }

  return {
    type: actionTypes.ERROR_ON_SIGN_IN,
    errorOnSignIn: true,
  };
};

export const fetchUserInfoError = () => {
  return {
    type: actionTypes.FETCH_USERINFO_ERROR,
  };
};

export const fetchCSRFFormToken = (
  headers: Record<string, string>,
  appVer: string,
  namespace: string,
  version: string,
  clientPromptLoginState: ClientPromptForLoginState
) => {
  return {
    type: actionTypes.FETCH_CSRF_FORM_TOKEN,
    headers,
    appver: appVer,
    namespace,
    version,
    clientPromptLoginState,
  };
};

export const fetchCSRFFormTokenError = () => {
  return {
    type: actionTypes.FETCH_CSRF_FORM_TOKEN_ERROR,
  };
};

export const handleGettingModalitiesComplete = () => {
  return {
    type: actionTypes.HANDLE_GETTING_MODALITIES_COMPLETE,
  };
};

export const updateModalitySectionExpando = (
  expandoState: boolean
) => {
  return {
    type: actionTypes.UPDATE_MODALITIES_SECTION_EXPANDO_STATE,
    isCollapsedModalities: expandoState,
  };
};

export const getBackgroundAuthUri = (provider: string) => {
  return {
    type: actionTypes.FETCH_BACKGROUNDAUTH_URI,
    provider,
  };
};

export const getBackgroundAuthUriSuccess = (
  authUri: string
) => {
  return {
    type: actionTypes.FETCH_BACKGROUNDAUTH_URI_SUCCESS,
    backgroundAuthUri: authUri,
  };
};

export const getUWPAuthInfoSuccess = (
  authInfo: AuthInfo
) => {
  return {
    type: actionTypes.FETCH_UWP_AUTHINFO_SUCCESS,
    authInfo,
  };
};

export const fetchAuthInfoError = (
  error: Error
) => {
  return {
    type: actionTypes.FETCH_USERINFO_SUCCESS,
    error,
  };
};

export const signInUser = () => {
  return {
    type: actionTypes.SIGNING_IN_USER,
  };
};

export const getInfoForClientTokenRetrieval = () => {
  return {
    type: actionTypes.GET_INFO_CLIENT_TOKEN_RETRIEVAL,
  };
};

export const getInfoForClientTokenRetrievalSuccess = (
  clientTokenRetrievalInfoModel: ClientTokenRetrievalInfoModel
) => {
  return {
    type: actionTypes.GET_INFO_CLIENT_TOKEN_RETRIEVAL_SUCCESS,
    clientTokenRetrievalInfoModel,
  };
};

export const showModalityExperience = (
  modalityLink: string
) => {
  return {
    type: actionTypes.SHOW_MODALITY_EXPERIENCE_IN_IFRAME,
    iframeModalityLink: modalityLink,
  };
};

export const authenticateUserSuccess = () => {
  return {
    type: actionTypes.FETCH_CSRF_FORM_TOKEN,
  };
};

export const signInUserBackground = (
  authInfo: AuthInfo
) => {
  return {
    authInfo,
    type: actionTypes.AUTHENTICATE_USER,
  };
};

export const signInUserIntoClientApp = () => {
  return {
    type: actionTypes.SIGN_IN_USER_INTO_CLIENT,
  };
};

export const signInUserIntoClientAppAggregator = (
  authInfo: AuthInfo
) => {
  return {
    type: actionTypes.SIGN_IN_USER_INTO_CLIENT_AGGREGATOR,
    authInfo,
  };
};

export const fetchUnAuthSchemaError = () => {
  return {
    type: actionTypes.FETCH_UNAUTH_SCHEMA_ERROR,
  };
};

export const fetchUnAuthSchemaSuccess = (
  result: AlchemyResult
) => {
  return {
    type: actionTypes.FETCH_UNAUTH_SCHEMA_SUCCESS,
    payload: result,
  };
};

export const initializeAlchemyUnAuthSchemaFetch = () => {
  return {
    type: actionTypes.INIT_ALCHEMY_UNAUTH_ON_SESSION_START,
  };
};

export const toggleUnAuthBranching = () => {
  return {
    type: actionTypes.TOGGLE_UNAUTH_BRANCHING,
  };
};

export const fetchAvailableModalitiesV2 = () => {
  return {
    type: actionTypes.FETCH_AVAILABLE_MODALITIES_V2,
  };
};

export const fetchAvailableModalitiesV2Success = (
  response: ModalityResponseModel
) => {
  return {
    type: actionTypes.FETCH_AVAILABLE_MODALITIES_V2_SUCCESS,
    response,
  };
};

export const fetchAvailableModalitiesV2Error = () => {
  return {
    type: actionTypes.FETCH_AVAILABLE_MODALITIES_V2_ERROR,
  };
};

export const updatePreferredTimeZone = (
  preferredTimeZone: string
) => {
  return {
    type: actionTypes.UPDATE_PREFERRED_TIMEZONE,
    preferredTimeZone,
  };
};

export const updatePreferredLocale = (
  preferredLocale: string
) => {
  return {
    type: actionTypes.UPDATE_PREFERRED_LOCALE,
    preferredLocale,
  };
};

export const updateIsPhoneNumberValid = (
  isPhoneNumberValid: boolean
) => {
  return {
    type: actionTypes.UPDATE_IS_PHONENUMBER_VALID,
    isPhoneNumberValid,
  };
};

export const updateIsEmailValid = (
  isEmailValid: boolean
) => {
  return {
    type: actionTypes.UPDATE_IS_EMAIL_VALID,
    isEmailValid,
  };
};

export const updateIsTitleValid = (isTitleValid: boolean) => {
  return {
    type: actionTypes.UPDATE_IS_TITLE_VALID,
    isTitleValid: isTitleValid,
  };
};

export const updateDraftRequest = (properties: Partial<FreelanceRequest>) => {
  return {
    type: actionTypes.UPDATE_DRAFT_REQUEST,
    properties,
  };
};

export const bypassThemeRouting = (dialogId: string) => {
  return {
    type: actionTypes.HANDLE_THEME_ROUTING,
    dialogId: dialogId,
  };
};

export const createRequest = () => {
  return {
    type: actionTypes.CREATE_REQUEST,
  };
};

export const createRequestSuccess = (
  request: FreelanceRequest
) => {
  return {
    type: actionTypes.CREATE_REQUEST_SUCCESS,
    request,
  };
};

export const createRequestError = (
  error: Error
) => {
  return {
    type: actionTypes.CREATE_REQUEST_ERROR,
    error,
  };
};

export const updateRaveChatWindowRequestCorrelationId = (
  correlationId: string
) => {
  return {
    type: actionTypes.UPDATE_LAST_OPENED_RAVE_CHAT_REQUEST_CORRELATIONID,
    correlationId,
  };
};

export const updateBusinessImpactConfirmed = (
  businessImpactConfirmed: boolean
) => {
  return {
    type: actionTypes.UPDATE_BUSINESS_IMPACT_CONFIRMED,
    businessImpactConfirmed,
  };
};

export const updateCustomerAvailabilityConfirmed = (
  customerAvailabilityConfirmed: boolean
) => {
  return {
    type: actionTypes.UPDATE_CUSTOMER_AVAILABILITY_CONFIRMED,
    customerAvailabilityConfirmed,
  };
};

export const navigateToPage = (page: ServiceRequestPage) => {
  return {
    type: actionTypes.PUSH_PAGE_STACK,
    page: page,
  };
};

export const navigateBack = (purpose?: ServiceRequestPageType) => {
  return {
    type: actionTypes.POP_PAGE_STACK,
    purpose: purpose,
  };
};

export const initializeToPage = (page: ServiceRequestPage) => {
  return {
    type: actionTypes.INITIALIZE_PAGE_STACK,
    page: page,
  };
};

export const removeAllPages = () => {
  return {
    type: actionTypes.EMPTY_PAGE_STACK,
  };
};

export const updateShowAgentModalitiesButton = (showAgentModalitiesButtonClicked: boolean) => {
  return {
    type: actionTypes.UPDATE_SHOW_AGENT_MODALITIES_BUTTON,
    showAgentModalitiesButtonClicked: showAgentModalitiesButtonClicked,
  };
};

export const updateSelectedEduProduct = (eduProductId: string) => {
  return {
    type: actionTypes.UPDATE_SELECTED_EDU_PRODUCT,
    eduProductId
  }
}